import { useMutation } from "@tanstack/solid-query";
import { toast } from "solid-sonner";
import { axiosInstance } from "@/api/client";
import {
  parseAttachmentFilename,
  triggerBlobDownload,
} from "@/api/utils/download";
import { getApiErrorMessage } from "@/api/utils/error";
import type { ReportFiltersPayload } from "../types";

// Kept in its OWN module (not an api barrel): it pulls `solid-sonner`, whose
// import touches `window`, which would break node-env unit tests — same reason
// as the talent-pool `anonymous-cv.ts` sibling.

export type AtsReportRequest = {
  jobOfferId: string;
  title?: string;
  prepared_for?: string;
  intro?: string;
  /** Quali candidature includere; assente = tutte. */
  filters?: ReportFiltersPayload;
};

export type ReportDownloadResult = {
  blob: Blob;
  filename: string;
  /** From `X-Report-Cache` — true when the server served the L3-cached PDF. */
  cacheHit: boolean;
  /** From `X-Report-Degraded` — true when some AI evaluations fell back. */
  degraded: boolean;
  /** From `X-Report-Degraded-Count` — how many candidates lost their comment. */
  degradedCount: number;
  /**
   * From `X-Report-Degraded-Causes` — why (`llm_error`, `only_real_skills`,
   * `no_pii_leakage`). Decides whether "try again" is honest advice.
   */
  degradedCauses: string[];
};

// biome-ignore lint/suspicious/noExplicitAny: axios response headers are loosely typed
const extractReportMeta = (headers: any) => ({
  filename: parseAttachmentFilename(
    headers["content-disposition"] as string | undefined,
    "report-candidati.pdf",
  ),
  cacheHit: headers["x-report-cache"] === "hit",
  degraded: headers["x-report-degraded"] === "true",
  degradedCount: Number(headers["x-report-degraded-count"] ?? 0) || 0,
  degradedCauses: String(headers["x-report-degraded-causes"] ?? "")
    .split(",")
    .map((cause) => cause.trim())
    .filter(Boolean),
});

// Base fetcher — ATS all-applicants report for one offer.
export const downloadAtsReport = async ({
  jobOfferId,
  ...body
}: AtsReportRequest): Promise<ReportDownloadResult> => {
  const response = await axiosInstance.post(
    `/v1/ats/job-offers/${jobOfferId}/report`,
    body,
    {
      responseType: "blob",
    },
  );
  return {
    blob: response.data as Blob,
    ...extractReportMeta(response.headers),
  };
};

const onReportSuccess = (result: ReportDownloadResult) => {
  triggerBlobDownload(result.blob, result.filename);
  if (result.degraded) {
    // Fino a quando gli header non erano esposti in CORS questo ramo non e' mai
    // partito: il recruiter scaricava un report con valutazioni mancanti e
    // leggeva un sereno «Report scaricato».
    const n = result.degradedCount;
    // Riprovare serve solo se il guasto era del servizio: un commento scartato
    // dai controlli (skill o dati personali) viene scartato di nuovo.
    const transientOnly =
      result.degradedCauses.length > 0 &&
      result.degradedCauses.every((cause) => cause === "llm_error");
    const hint = transientOnly
      ? "rigenerando più tardi verrà incluso"
      : result.degradedCauses.length > 0
        ? "non ha superato i controlli di sicurezza: rigenerare non cambia l'esito"
        : "";
    toast.info(
      n > 0
        ? `Report scaricato. ${n} ${n === 1 ? "candidato è" : "candidati sono"} senza valutazione AI: per ${n === 1 ? "lui" : "loro"} resta il confidence score${hint ? ` (${hint})` : ""}.`
        : "Report scaricato. Alcune valutazioni AI non erano disponibili: incluso solo il confidence score.",
    );
  } else {
    toast.success("Report scaricato");
  }
};

const onReportError = (error: unknown) => {
  // 422 = i filtri erano validi e non hanno selezionato nessuno. E' un esito,
  // non un guasto: il dialog lo previene, questo e' solo la rete.
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 422) {
    toast.info("Nessun candidato corrisponde ai filtri selezionati.");
    return;
  }
  toast.error(getApiErrorMessage(error, "Generazione del report non riuscita"));
};

// Custom hook — ATS all-applicants report.
export const useDownloadAtsReport = () =>
  useMutation(() => ({
    mutationFn: (input: AtsReportRequest) => downloadAtsReport(input),
    onSuccess: onReportSuccess,
    onError: onReportError,
  }));
